import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Award, BarChart3, Calendar, CalendarDays, CheckCircle2, Clock, Download, Layers, ListChecks, Plus, Shield, Tags, User, Users, XCircle,
} from 'lucide-react';
import * as hr from '../api';
import Loader from '../components/Loader';
import { download, toCSV } from '../format';
import { Kpi } from './attendance/shared';
import { STATUS_LABEL, WAITING, dmy } from './leave/shared';
import { LeaveRequests, ManageRequests, RequestView } from './leave/Requests';
import ApplyForm from './leave/ApplyForm';
import Balances from './leave/Balances';
import LeaveCalendar from './leave/LeaveCalendar';
import { Holidays, LeaveTypes, Setup } from './leave/Setup';
import AdvancedDashboard from './leave/AdvancedDashboard';
import Reports from './leave/Reports';

// HR > Leave Management (Stephanet's LeaveManagement.jsx): leave requests, approvals, balances, calendar, policy set-up, monetization
// and reports on the live leave records. Approvals run in the workflow engine (LEAVE_REQUEST: supervisor, then HR; LEAVE_MONETIZATION);
// balances are worked out on the server from requests, adjustments, carry-forwards and monetizations.

const TABS = [
  ['dashboard', 'Advanced Dashboard', Layers], ['requests', 'Leave Requests', Clock], ['balances', 'Leave Balances', Users],
  ['calendar', 'Calendar', Calendar], ['setup', 'Setup', Award], ['manage', 'Manage Requests', Shield], ['apply', 'Apply for Leave', Plus],
  ['add', 'Add for Employee', User], ['types', 'Leave Types', Tags], ['holidays', 'Holiday Setup', CalendarDays], ['reports', 'Reports', BarChart3],
];

export default function LeaveManagement() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'requests';
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [tick, setTick] = useState(0);
  const [viewing, setViewing] = useState(null);
  const [manageFilter, setManageFilter] = useState(null);

  const load = useCallback(() => hr.getLeaveScreen(year).then(d => { setData(d); setError(''); setTick(t => t + 1); return d; }).catch(e => setError(e.message)), [year]);
  useEffect(() => { load(); }, [load]);
  const go = (t, opts) => {
    if (t === 'manage') setManageFilter(opts || null);
    setParams(p => { const n = new URLSearchParams(p); if (t === 'requests') n.delete('tab'); else n.set('tab', t); return n; });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (error && !data) return <div className="mdr"><div className="mdr-card mdr-empty">{error}</div></div>;
  if (!data) return <Loader title="Loading leave management…" subtitle="Fetching requests and balances" />;

  const reqs = data.requests;
  const pending = reqs.filter(r => WAITING.includes(r.status)).length;
  const k = { total: reqs.length, approved: reqs.filter(r => r.status === 'approved').length, rejected: reqs.filter(r => r.status === 'rejected').length };
  const exportAll = () => download(`leave-requests-${data.today}.csv`, toCSV([
    { label: 'Leave ID', get: r => r.reference }, { label: 'Employee ID', get: r => r.employee_no }, { label: 'Employee Name', get: r => r.name },
    { label: 'Department', get: r => r.department }, { label: 'Grade', get: r => r.grade }, { label: 'Leave Type', get: r => r.leave_type },
    { label: 'Start Date', get: r => r.start_date }, { label: 'End Date', get: r => r.end_date }, { label: 'Working Days', get: r => r.days },
    { label: 'Status', get: r => STATUS_LABEL[r.status] }, { label: 'Paid', get: r => (r.paid ? 'Paid' : 'Unpaid') }, { label: 'Reason', get: r => r.reason },
    { label: 'Applied', get: r => r.applied_on }, { label: 'Approved By', get: r => r.approved_by }, { label: 'Comment', get: r => r.comment },
  ], reqs));
  const shared = { data, reload: load, year, setYear, go, onView: setViewing, tick };

  return (
    <div className="mdr mds lvm">
      <div className="mdr-top">
        <div>
          <h2>Leave Management</h2>
          <p>Leave requests, approvals, balances, and policy configuration</p>
        </div>
        <div className="mdr-top-actions no-print">
          {data.can.export && <button className="mdr-btn" onClick={exportAll} disabled={!reqs.length}><Download size={14} /> Export</button>}
          {(data.can.create || data.can.edit) && <button className="mdr-btn att2-mark" onClick={() => go('apply')}><Plus size={14} /> Apply Leave</button>}
        </div>
      </div>

      <div className="md-kpis mds-kpis lvm-kpis" key={`k${tick}`}>
        <Kpi label="Total Requests" value={k.total} Ico={ListChecks} tone="blue" note="All applications" />
        <Kpi label="Pending Approval" value={pending} Ico={Clock} tone="amber" note={pending ? 'Action required' : 'Up to date'} i={1} />
        <Kpi label="Approved" value={k.approved} Ico={CheckCircle2} tone="green" note="Active & processed" i={2} />
        <Kpi label="Rejected" value={k.rejected} Ico={XCircle} tone="red" note="Declined" i={3} />
      </div>

      {pending > 0 && data.can.approve && (
        <div className="lvm-banner no-print">
          <span><Clock size={15} /> <b>{pending}</b> leave request(s) awaiting your approval</span>
          <button className="mds-run sm" onClick={() => go('manage', { status: 'waiting' })}>Review Now</button>
        </div>
      )}

      <div className="mds-tabs no-print" role="tablist">
        {TABS.map(([key, label, Ico]) => (
          <button key={key} type="button" role="tab" className={tab === key ? 'active' : ''} onClick={() => go(key)}>
            <Ico size={14} /> {label}{key === 'requests' && pending > 0 && <span className="lvm-badge">{pending}</span>}
          </button>
        ))}
      </div>

      {tab === 'dashboard' && <AdvancedDashboard {...shared} />}
      {tab === 'requests' && <LeaveRequests {...shared} />}
      {tab === 'balances' && <Balances {...shared} />}
      {tab === 'calendar' && <LeaveCalendar {...shared} />}
      {tab === 'setup' && <Setup {...shared} />}
      {tab === 'manage' && <ManageRequests {...shared} initial={manageFilter} />}
      {tab === 'apply' && <ApplyForm {...shared} mode="apply" />}
      {tab === 'add' && <ApplyForm {...shared} mode="add" />}
      {tab === 'types' && <LeaveTypes {...shared} />}
      {tab === 'holidays' && <Holidays {...shared} />}
      {tab === 'reports' && <Reports {...shared} />}

      {viewing && <RequestView id={viewing} data={data} reload={load} onClose={() => setViewing(null)} />}
      <p className="print-only mds-print-period">Printed {dmy(data.today)}</p>
    </div>
  );
}
